<script setup lang="ts">
import { checkUserEmailForLoginOrSignup } from "~/utils/fetch/account/userCheck"
import { loginOrSignUpCheckedEmailStorageKey } from "~/utils/loginSignup/loginSignup"
import Cookies from "js-cookie"
import { storeToRefs } from "pinia"
import { useUserStore } from "~/stores/user"

// Modal states: 'check' | 'login' | 'signup' | 'reset-password' | 'password-confirmation'
const currentStep =
  ref<
    "check" | "login" | "signup" | "reset-password" | "password-confirmation"
  >("check")
const userEmail = ref("")
const isLoading = ref(false)

const emit =
  defineEmits<{
    (e: "userLoggedIn"): void
    (e: "close"): void
  }>()

// Handles custom email check flow for the modal
const handleEmailCheck = async (email: string) => {
  try {
    isLoading.value = true
    userEmail.value = email
    const userCheckResponse = await checkUserEmailForLoginOrSignup(email)

    Cookies.set(loginOrSignUpCheckedEmailStorageKey, email)

    if (userCheckResponse.isUser || userCheckResponse.isLegacyUser) {
      currentStep.value = "login"
    } else {
      currentStep.value = "signup"
    }
  } catch (e) {
    Cookies.remove(loginOrSignUpCheckedEmailStorageKey)
    console.error("Error checking email:", e)
  } finally {
    isLoading.value = false
  }
}

// Go back to email check
const goBackToEmailCheck = async () => {
  currentStep.value = "check"
  // Keep the pre-filled email instead of clearing it
  // userEmail.value = ""

  // Automatic focus on email field when going back
  await nextTick()
  const emailInput = document.getElementById("email-input")
  if (emailInput) {
    emailInput.focus()
  }
}

// Go to reset password form
const goToResetPassword = () => {
  currentStep.value = "reset-password"
}

// Return to login from password confirmation
const goToLogin = () => {
  currentStep.value = "login"
}

// Handle password reset request
const handlePasswordForgetRequest = async (email: string) => {
  try {
    isLoading.value = true
    const { data, error } = await useFetch(
      `/api/account/link-reset-password/${encodeURIComponent(email)}`
    )
    if (data.value) {
      // Show confirmation screen inside the modal
      currentStep.value = "password-confirmation"
    }
    if (error.value) {
      // Handle the error
      console.error("Error sending reset link:", error.value)
    }
  } catch (error) {
    console.error("Error sending reset link:", error)
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <div class="modal-login-signup-wrapper">
    <!-- Email Check -->
    <div v-if="currentStep === 'check'" class="p-4">
      <LoginSignupOrganismSocialLogin />
      <UtilsLoginOrSignupOrText />
      <UtilsLoginOrSignupPageIntroText />
      <LoginSignupOrganismFormUserCheck
        :email="userEmail"
        :handle-submit="({ email }: { email: string }) => handleEmailCheck(email)"
        :is-loading="isLoading"
      />
    </div>

    <div v-else class="p-4">
      <div class="mb-4">
        <button
          @click="goBackToEmailCheck"
          class="text-green-600 hover:text-green-700 flex items-center"
        >
          <UtilsIcon name="ArrowIosLeft.svg" class="mr-1 h-4 w-4" />
          {{ $t("loginSignup.back") }}
        </button>
      </div>
      <!-- Login -->
      <PagesLoginOrSignupModalLoginInput
        v-if="currentStep === 'login'"
        :default-email="userEmail"
        :has-disabled-email="true"
        :show-policies-disclaimer="false"
        :has-items-with-default-widhts="false"
        @login-success="emit('userLoggedIn')"
        @on-edit="goBackToEmailCheck"
        @on-forgot-password="goToResetPassword"
      />
      <!-- Signup -->
      <PagesLoginOrSignupModalRegistrationInput
        v-else-if="currentStep === 'signup'"
        :default-email="userEmail"
        :has-disabled-email="true"
        :show-policies-disclaimer="false"
        :has-items-with-default-widhts="false"
        @registration-success="emit('userLoggedIn')"
        @on-edit="goBackToEmailCheck"
      />
      <!-- Reset Password -->
      <DialogsAndModalsUtilsPasswordForget
        v-else-if="currentStep === 'reset-password'"
        :has-items-with-default-widhts="false"
        :emit-on-edit="true"
        @on-password-forget="handlePasswordForgetRequest"
        @on-edit-email="goBackToEmailCheck"
      />
      <!-- Password Confirmation -->
      <DialogsAndModalsUtilsPasswordConfirmation
        v-else-if="currentStep === 'password-confirmation'"
        :email="userEmail"
        :in-modal="true"
        @go-to-login="goToLogin"
      />
    </div>
  </div>
</template>
